import type {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridOptions,
  MenuItemDef
} from "ag-grid-enterprise";
import { createContext, useContext } from "react";

/**
 * The bridge between data grids and the app's feedback capture, defined
 * here so grid wrappers can consume it WITHOUT ui-kit depending on the
 * feedback package (the feedback modal is itself built from ui-kit — the
 * dependency must point one way). The feedback package provides the value;
 * unwired trees (tests, storybook) see null and grids behave exactly as
 * before.
 */

/**
 * Domain identity extractor a grid supplies alongside its rows: given a
 * row's data, the identity keys a report should carry (e.g. Tradex's style
 * option + location ids). Merged into each captured cell.
 */
export type GridRowDescriptor = (
  row: Record<string, unknown>
) => Record<string, unknown>;

/**
 * `GetContextMenuItemsParams` with `defaultItems` widened to full menu
 * items, so a consumer's own (possibly custom) items can ride through the
 * integration unchanged.
 */
export type GridFeedbackMenuParams = Omit<
  GetContextMenuItemsParams,
  "defaultItems"
> & {
  defaultItems?: (DefaultMenuItem | MenuItemDef)[];
};

export interface GridFeedbackIntegration {
  /**
   * The grid's context-menu items with "Report this value" appended after
   * `params.defaultItems` (pass the consumer's own items there to keep
   * them). The item opens the feedback modal prefilled with the clicked
   * cell — and the full range selection, when one spans multiple cells.
   */
  buildMenuItems: (
    params: GridFeedbackMenuParams,
    describeRow?: GridRowDescriptor
  ) => (DefaultMenuItem | MenuItemDef)[];
}

export const GridFeedbackContext =
  createContext<GridFeedbackIntegration | null>(null);

/** The grid feedback integration, or null when no provider wired one up. */
export function useGridFeedback(): GridFeedbackIntegration | null {
  return useContext(GridFeedbackContext);
}

/**
 * Wrap a grid's options so its context menu carries "Report this value":
 * the consumer's own `getContextMenuItems` (or AG Grid's defaults) still
 * produce the base items, the integration appends the report entry. With
 * no integration the options pass through untouched — exactly today's
 * behavior for unwired trees.
 */
export function withGridFeedbackMenu(
  gridOptions: GridOptions | undefined,
  feedback: GridFeedbackIntegration | null,
  describeRow?: GridRowDescriptor
): GridOptions | undefined {
  if (!feedback) return gridOptions;
  const base = gridOptions?.getContextMenuItems;
  return {
    ...gridOptions,
    getContextMenuItems: (params) => {
      const items = base ? base(params) : (params.defaultItems ?? []);
      // The consumer's getter may be async (AG Grid allows a Promise).
      if (items instanceof Promise) {
        return items.then((resolved) =>
          feedback.buildMenuItems(
            { ...params, defaultItems: resolved },
            describeRow
          )
        );
      }
      return feedback.buildMenuItems(
        { ...params, defaultItems: items },
        describeRow
      );
    }
  };
}
